import React, { useState, useRef, useEffect, useMemo } from 'react';
import axiosInstance from '@/lib/axios';
import axios from 'axios';
import { 
  Upload, FileText, Clipboard, Sparkles, Loader2, CheckCircle2, 
  AlertCircle, X, Home, Euro, Ruler, Building2, FileCheck, Edit3,
  Image as ImageIcon, FileText as FileTextIcon, Type
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Editable field component with uncertainty support
function EditableField({ label, value, onChange, type = 'text', options = [], placeholder = '', required = false, uncertain = false, uncertainReason = '' }) {
  const isEmpty = value === null || value === undefined || value === '';
  const showRequired = required && isEmpty;
  const showUncertain = uncertain && !isEmpty;
  
  const baseInputClass = "w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none";
  const stateClass = showRequired 
    ? 'border-red-300 bg-red-50 text-red-900' 
    : showUncertain 
      ? 'border-orange-300 bg-orange-50 text-orange-900' 
      : 'border-slate-300 bg-white text-slate-900';
  
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-slate-600 font-medium flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
        {showUncertain && <span className="text-orange-500" title={uncertainReason}>⚠️</span>}
      </label>
      {type === 'select' ? (
        // Use radio buttons instead of select for Safari compatibility
        <div className={`flex flex-wrap gap-2 p-2 rounded-lg ${showUncertain ? 'bg-orange-50 border border-orange-300' : 'bg-slate-100'}`}>
          {options.map(opt => (
            <label 
              key={opt.value} 
              className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer text-sm transition-all ${
                value === opt.value 
                  ? 'bg-amber-500 text-white font-medium' 
                  : 'bg-white text-slate-700 hover:bg-slate-200 border border-slate-200'
              }`}
            >
              <input
                type="radio"
                name={label}
                value={opt.value}
                checked={value === opt.value}
                onChange={(e) => onChange(e.target.value)}
                className="sr-only"
              />
              {opt.label}
            </label>
          ))}
        </div>
      ) : type === 'date' ? (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${baseInputClass} ${stateClass}`}
        />
      ) : (
        <input
          type={type}
          value={value ?? ''}
          onChange={(e) => onChange(type === 'number' ? (e.target.value ? Number(e.target.value) : null) : e.target.value)}
          placeholder={placeholder || (showRequired ? 'À compléter' : '')}
          className={`${baseInputClass} ${stateClass}`}
        />
      )}
      {showUncertain && uncertainReason && (
        <p className="text-xs text-orange-600 mt-0.5">{uncertainReason}</p>
      )}
    </div>
  );
}

/**
 * Confidence banner + chips shown right after a successful extraction.
 * Gives the user a clear visual reassurance of what the AI was able to read.
 */
function ExtractionConfidenceBanner({ extractedData, simulationFields, sourceType }) {
  if (!extractedData && !simulationFields) return null;

  // Confidence score (0..1) — fallback to a sensible default if model omitted it
  const rawScore = typeof extractedData?.confidence_score === 'number'
    ? extractedData.confidence_score
    : (simulationFields?._extraction?.confidence ?? null);
  const score = rawScore != null ? Math.max(0, Math.min(1, rawScore)) : null;
  const scorePct = score != null ? Math.round(score * 100) : null;

  // Tier (colour + label)
  let tier = { label: 'Analyse partielle', color: 'amber', bar: 'bg-amber-500' };
  if (scorePct === null) tier = { label: 'Analyse terminée', color: 'blue', bar: 'bg-blue-500' };
  else if (scorePct >= 85) tier = { label: 'Annonce détectée', color: 'emerald', bar: 'bg-emerald-500' };
  else if (scorePct >= 60) tier = { label: 'Bonne extraction', color: 'blue', bar: 'bg-blue-500' };

  // Source icon
  const SourceIcon = sourceType === 'image' ? ImageIcon : sourceType === 'pdf' ? FileTextIcon : Type;
  const sourceLabel = sourceType === 'image' ? 'Vision IA (photo)' : sourceType === 'pdf' ? 'OCR PDF' : 'Texte';

  // Build chips from simulationFields — green for found, gray for missing
  const fields = simulationFields || {};
  const features = extractedData?.features || {};

  const fmtNum = (v, suffix = '') => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) return String(v) + suffix;
    return new Intl.NumberFormat('fr-FR').format(n) + suffix;
  };

  const candidates = [
    { key: 'city', label: 'Ville', value: fields.city },
    { key: 'postalCode', label: 'CP', value: fields.postalCode },
    { key: 'propertyType', label: 'Type', value: fields.propertyType },
    { key: 'surface', label: 'Surface', value: fmtNum(fields.surface, ' m²') },
    { key: 'rooms', label: 'Pièces', value: fields.rooms },
    { key: 'bedrooms', label: 'Chambres', value: fields.bedrooms },
    { key: 'purchasePrice', label: 'Prix', value: fmtNum(fields.purchasePrice, ' €') },
    { key: 'monthlyCharges', label: 'Charges', value: fmtNum(fields.monthlyCharges, ' €/mois') },
    { key: 'dpeRating', label: 'DPE', value: fields.dpeRating },
    { key: 'yearBuilt', label: 'Année', value: fields.yearBuilt },
    { key: 'floor', label: 'Étage', value: fields.floor },
    { key: 'hasElevator', label: 'Ascenseur', value: fields.hasElevator ? '✓' : null },
    { key: 'hasBalcony', label: 'Balcon', value: features.balcony ? '✓' : null },
    { key: 'hasTerrace', label: 'Terrasse', value: features.terrace ? '✓' : null },
    { key: 'hasParking', label: 'Parking', value: fields.hasParking ? '✓' : null },
    { key: 'hasCellar', label: 'Cave', value: features.cellar ? '✓' : null },
    { key: 'hasGarden', label: 'Jardin', value: features.garden ? '✓' : null },
    { key: 'agencyName', label: 'Agence', value: fields.agencyName },
    { key: 'notaryName', label: 'Notaire', value: fields.notaryName },
  ];

  const found = candidates.filter(c => c.value !== null && c.value !== undefined && c.value !== '' && c.value !== false);
  const missing = candidates.length - found.length;

  const tierBg = {
    emerald: 'from-emerald-50 to-teal-50 border-emerald-200',
    blue: 'from-blue-50 to-sky-50 border-blue-200',
    amber: 'from-amber-50 to-orange-50 border-amber-200',
  }[tier.color];
  const tierText = {
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
    amber: 'text-amber-700',
  }[tier.color];

  return (
    <div
      className={`bg-gradient-to-br ${tierBg} border rounded-xl p-4 mb-2`}
      data-testid="extraction-confidence-banner"
    >
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className={`h-5 w-5 ${tierText}`} />
          <span className={`font-semibold ${tierText}`}>{tier.label}</span>
          {scorePct !== null && (
            <span
              className={`ml-1 text-xs font-bold px-2 py-0.5 rounded-full bg-white ${tierText}`}
              data-testid="extraction-confidence-score"
            >
              {scorePct}%
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <SourceIcon className="h-4 w-4" />
          <span>{sourceLabel}</span>
        </div>
      </div>

      {/* Progress bar */}
      {scorePct !== null && (
        <div className="w-full h-1.5 bg-white/60 rounded-full overflow-hidden mb-3">
          <div
            className={`h-full ${tier.bar} transition-all duration-700`}
            style={{ width: `${scorePct}%` }}
          />
        </div>
      )}

      {/* Chips */}
      {found.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="extraction-chips">
          {found.map(chip => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded-full text-xs border border-emerald-200 text-emerald-800 font-medium shadow-sm"
              data-testid={`extraction-chip-${chip.key}`}
            >
              <span className="text-slate-500 font-normal">{chip.label}</span>
              <span className="text-emerald-900">{String(chip.value)}</span>
            </span>
          ))}
          {missing > 0 && (
            <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-full text-xs text-slate-500 border border-slate-200">
              + {missing} champ{missing > 1 ? 's' : ''} à compléter
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * PropertyAutoFill Component
 * Allows users to automatically fill property data from text or documents
 */
export default function PropertyAutoFill({ onDataExtracted, onClose }) {
  const [activeTab, setActiveTab] = useState('text');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState('agency_sheet');
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editedData, setEditedData] = useState({});
  
  const fileInputRef = useRef(null);

  // Initialize edited data when extraction completes
  useEffect(() => {
    if (extractedData?.simulation_fields) {
      const fields = extractedData.simulation_fields;
      console.log('[PropertyAutoFill] Extracted data received:', {
        city: fields.city,
        postalCode: fields.postalCode,
        surface: fields.surface,
        purchasePrice: fields.purchasePrice,
        rooms: fields.rooms
      });
      setEditedData({
        propertyType: fields.propertyType || 'maison',
        city: fields.city || '',
        postalCode: fields.postalCode || '',
        surface: fields.surface,
        rooms: fields.rooms,
        bedrooms: fields.bedrooms,
        purchasePrice: fields.purchasePrice,
        monthlyCharges: fields.monthlyCharges,
        dpeRating: fields.dpeRating || '',
        yearBuilt: fields.yearBuilt,
        address: fields.address || '',
        agencyName: fields.agencyName || '',
        notaryName: fields.notaryName || '',
        signatureDate: fields.signatureDate || '',
        finalSaleDeadline: fields.finalSaleDeadline || '',
        _extraction: fields._extraction
      });
    } else {
      console.log('[PropertyAutoFill] No simulation_fields in extractedData:', extractedData);
    }
  }, [extractedData]);

  // Calculate missing required fields
  const missingFields = useMemo(() => {
    const required = [
      { key: 'city', label: 'Ville' },
      { key: 'postalCode', label: 'Code postal' },
      { key: 'surface', label: 'Surface' },
      { key: 'purchasePrice', label: 'Prix' }
    ];
    return required
      .filter(f => !editedData[f.key] && editedData[f.key] !== 0)
      .map(f => f.label);
  }, [editedData]);

  // Get uncertain fields from extraction
  const uncertainFields = useMemo(() => {
    const uncertain = editedData._extraction?.uncertain_fields || [];
    // Convert array to map for easy lookup
    const map = {};
    uncertain.forEach(u => {
      map[u.field] = u.reason;
    });
    return map;
  }, [editedData._extraction]);

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleTextExtract = async () => {
    if (!text.trim() || text.length < 50) {
      toast.error('Veuillez coller un texte plus détaillé (minimum 50 caractères)');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `${API}/property/extract-from-text`,
        { text, source_type: 'text' },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setExtractedData(response.data);
        
        // DIRECTLY initialize editedData here (Safari fix)
        const fields = response.data.simulation_fields || {};
        setEditedData({
          propertyType: fields.propertyType || 'maison',
          city: fields.city || '',
          postalCode: String(fields.postalCode || ''),
          surface: fields.surface,
          rooms: fields.rooms,
          bedrooms: fields.bedrooms,
          purchasePrice: fields.purchasePrice,
          monthlyCharges: fields.monthlyCharges,
          dpeRating: fields.dpeRating || '',
          yearBuilt: fields.yearBuilt,
          address: fields.address || '',
          agencyName: fields.agencyName || '',
          notaryName: fields.notaryName || '',
          signatureDate: fields.signatureDate || '',
          finalSaleDeadline: fields.finalSaleDeadline || '',
          _extraction: fields._extraction
        });
        
        setShowPreview(true);
        toast.success('Données extraites ! Vérifiez et complétez si nécessaire.');
      } else {
        toast.error(response.data.message || 'Erreur d\'extraction');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'extraction');
    } finally {
      setLoading(false);
    }
  };

  const handleFileExtract = async () => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      
      const response = await axiosInstance.post(
        `${API}/property/extract-from-file`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      if (response.data.success) {
        console.log('[PropertyAutoFill] Extraction success, full response:', response.data);
        console.log('[PropertyAutoFill] simulation_fields:', response.data.simulation_fields);
        
        // Set extracted data for display
        setExtractedData(response.data);
        
        // DIRECTLY initialize editedData here instead of relying on useEffect
        // This fixes Safari state update issues
        const fields = response.data.simulation_fields || {};
        const newEditedData = {
          propertyType: fields.propertyType || 'maison',
          city: fields.city || '',
          postalCode: String(fields.postalCode || ''),
          surface: fields.surface,
          rooms: fields.rooms,
          bedrooms: fields.bedrooms,
          purchasePrice: fields.purchasePrice,
          monthlyCharges: fields.monthlyCharges,
          dpeRating: fields.dpeRating || '',
          yearBuilt: fields.yearBuilt,
          address: fields.address || '',
          agencyName: fields.agencyName || '',
          notaryName: fields.notaryName || '',
          signatureDate: fields.signatureDate || '',
          finalSaleDeadline: fields.finalSaleDeadline || '',
          _extraction: fields._extraction,
          // Premium document info
          _premiumDocumentUsed: response.data.extra_charge > 0,
          _premiumDocumentType: response.data.document_type,
          _premiumDocumentUrl: response.data.premium_document_url,
          _timestamp: Date.now() // Force re-render
        };
        console.log('[PropertyAutoFill] Setting editedData:', newEditedData);
        
        // Force state update by resetting first
        setEditedData({});
        setTimeout(() => {
          setEditedData(newEditedData);
          setShowPreview(true);
        }, 50);
        
        // Show premium charge message if applicable
        if (response.data.extra_charge > 0) {
          toast.success(`${response.data.message} - Total: 5,99€`, { duration: 6000 });
        } else {
          toast.success(response.data.message);
        }
      } else {
        console.log('[PropertyAutoFill] Extraction failed:', response.data);
        toast.error(response.data.message || 'Erreur d\'extraction');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'extraction');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyData = () => {
    // Pass the edited data (with user modifications)
    const dataToApply = {
      ...editedData,
      _extraction: extractedData?.simulation_fields?._extraction
    };
    console.log('[PropertyAutoFill] Applying data to form:', dataToApply);
    onDataExtracted(dataToApply);
    toast.success('Données appliquées au formulaire');
    onClose();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10MB)');
        return;
      }
      setFile(selectedFile);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Remplissage automatique</h2>
                <p className="text-amber-100 text-sm">
                  Importez les données du bien automatiquement
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {!showPreview ? (
            <>
              {/* Tabs */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setActiveTab('text')}
                  data-testid="autofill-tab-text"
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
                    activeTab === 'text' 
                      ? 'bg-amber-100 text-amber-700 border-2 border-amber-300' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Clipboard className="h-5 w-5" />
                  Copier-coller
                </button>
                <button
                  onClick={() => setActiveTab('file')}
                  data-testid="autofill-tab-file"
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
                    activeTab === 'file' 
                      ? 'bg-amber-100 text-amber-700 border-2 border-amber-300' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Upload className="h-5 w-5" />
                  Importer un fichier
                </button>
              </div>

              {/* Text Tab */}
              {activeTab === 'text' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-700">
                      <strong>Collez</strong> le texte d'une annonce immobilière ou la description de l'agence.
                    </p>
                  </div>
                  
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    data-testid="autofill-text-input"
                    placeholder="Collez ici le texte de l'annonce..."
                    className="w-full h-48 p-4 border border-slate-300 rounded-xl resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">{text.length} caractères</span>
                    <Button
                      onClick={handleTextExtract}
                      disabled={loading || text.length < 50}
                      data-testid="autofill-analyze-text-btn"
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      {loading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse...</>
                      ) : (
                        <><Sparkles className="h-4 w-4 mr-2" />Analyser</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* File Tab */}
              {activeTab === 'file' && (
                <div className="space-y-4">
                  {/* Document Type Selection */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-slate-700">Type de document</label>
                    <div className="grid grid-cols-1 gap-3">
                      <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        documentType === 'agency_sheet' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                        <input type="radio" name="docType" value="agency_sheet" checked={documentType === 'agency_sheet'} onChange={(e) => setDocumentType(e.target.value)} className="text-amber-500" />
                        <FileText className="h-5 w-5 text-amber-600" />
                        <div className="flex-1">
                          <div className="font-medium">Fiche agence</div>
                          <div className="text-sm text-slate-500">Document de présentation</div>
                        </div>
                        <span className="text-green-600 font-medium">Gratuit</span>
                      </label>
                      
                      <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        documentType === 'promesse_vente' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                        <input type="radio" name="docType" value="promesse_vente" checked={documentType === 'promesse_vente'} onChange={(e) => setDocumentType(e.target.value)} className="text-amber-500" />
                        <FileCheck className="h-5 w-5 text-blue-600" />
                        <div className="flex-1">
                          <div className="font-medium">Promesse de vente</div>
                          <div className="text-sm text-slate-500">Analyse complète</div>
                        </div>
                        <span className="text-amber-600 font-medium">+1€</span>
                      </label>
                    </div>
                  </div>

                  {/* File Upload */}
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                      file ? 'border-green-500 bg-green-50' : 'border-slate-300 hover:border-amber-500'
                    }`}
                  >
                    <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleFileChange} className="hidden" />
                    {file ? (
                      <div className="flex items-center justify-center gap-3">
                        <CheckCircle2 className="h-8 w-8 text-green-500" />
                        <div className="text-left">
                          <p className="font-medium">{file.name}</p>
                          <p className="text-sm text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="p-1 hover:bg-slate-200 rounded">
                          <X className="h-4 w-4 text-slate-500" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                        <p className="font-medium text-slate-700">Cliquez pour sélectionner</p>
                        <p className="text-sm text-slate-500 mt-1">PDF ou photo/capture d'annonce (JPG, PNG, WebP — max 10MB)</p>
                        <p className="text-xs text-slate-400 mt-1">L'IA analyse les images : prix, surface, état, atouts visibles…</p>
                      </>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleFileExtract} disabled={loading || !file} data-testid="autofill-analyze-file-btn" className="bg-amber-500 hover:bg-amber-600">
                      {loading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse...</>
                      ) : (
                        <><Sparkles className="h-4 w-4 mr-2" />Analyser{documentType === 'promesse_vente' && ' (+1€)'}</>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Preview & Edit */
            <div className="space-y-4" key={extractedData?.extracted_at || 'preview'}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                  <h3 className="text-lg font-semibold">Données extraites</h3>
                </div>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                  <Edit3 className="h-3 w-3" /> Modifiable
                </span>
              </div>

              {/* Confidence Banner + Chips (visual feedback for AI extraction) */}
              <ExtractionConfidenceBanner
                extractedData={extractedData?.extracted_data}
                simulationFields={extractedData?.simulation_fields}
                sourceType={extractedData?.extracted_data?.source_type || extractedData?.simulation_fields?._extraction?.source_type}
              />

              {extractedData?.simulation_fields?._extraction?.notes && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    {extractedData.simulation_fields._extraction.notes}
                  </p>
                </div>
              )}

              {/* Editable Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Property */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Home className="h-4 w-4" /> Bien
                  </h4>
                  <div className="space-y-3">
                    <EditableField label="Type" value={editedData.propertyType} onChange={(v) => handleFieldChange('propertyType', v)} type="select"
                      options={[{ value: 'maison', label: 'Maison' }, { value: 'appartement', label: 'Appartement' }, { value: 'terrain', label: 'Terrain' }]} />
                    <EditableField label="Ville" value={editedData.city} onChange={(v) => handleFieldChange('city', v)} required />
                    <EditableField label="Code postal" value={editedData.postalCode} onChange={(v) => handleFieldChange('postalCode', v)} required />
                  </div>
                </div>

                {/* Surface */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Ruler className="h-4 w-4" /> Surfaces
                  </h4>
                  <div className="space-y-3">
                    <EditableField 
                      label="Surface (m²)" 
                      value={editedData.surface} 
                      onChange={(v) => handleFieldChange('surface', v)} 
                      type="number" 
                      placeholder="À vérifier" 
                      required 
                      uncertain={!!uncertainFields.surface}
                      uncertainReason={uncertainFields.surface}
                    />
                    <EditableField label="Pièces" value={editedData.rooms} onChange={(v) => handleFieldChange('rooms', v)} type="number" />
                    <EditableField label="Chambres" value={editedData.bedrooms} onChange={(v) => handleFieldChange('bedrooms', v)} type="number" />
                  </div>
                </div>

                {/* Price */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Euro className="h-4 w-4" /> Prix
                  </h4>
                  <div className="space-y-3">
                    <EditableField label="Prix (€)" value={editedData.purchasePrice} onChange={(v) => handleFieldChange('purchasePrice', v)} type="number" required />
                    <EditableField label="Charges/mois (€)" value={editedData.monthlyCharges} onChange={(v) => handleFieldChange('monthlyCharges', v)} type="number" />
                  </div>
                </div>

                {/* Energy */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4" /> Énergie
                  </h4>
                  <div className="space-y-3">
                    <EditableField 
                      label="DPE" 
                      value={editedData.dpeRating} 
                      onChange={(v) => handleFieldChange('dpeRating', v)} 
                      type="select"
                      options={[{ value: '', label: 'Non renseigné' }, { value: 'A', label: 'A' }, { value: 'B', label: 'B' }, { value: 'C', label: 'C' }, { value: 'D', label: 'D' }, { value: 'E', label: 'E' }, { value: 'F', label: 'F' }, { value: 'G', label: 'G' }]} 
                      uncertain={!!uncertainFields.dpeRating}
                      uncertainReason={uncertainFields.dpeRating}
                    />
                    <EditableField 
                      label="Année" 
                      value={editedData.yearBuilt} 
                      onChange={(v) => handleFieldChange('yearBuilt', v)} 
                      type="number" 
                      placeholder="ex: 1949"
                      uncertain={!!uncertainFields.yearBuilt}
                      uncertainReason={uncertainFields.yearBuilt}
                    />
                  </div>
                </div>
              </div>

              {/* Contacts & Dates Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Contacts */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <span className="text-lg">👤</span> Contacts
                  </h4>
                  <div className="space-y-3">
                    <EditableField label="Agent immobilier" value={editedData.agencyName} onChange={(v) => handleFieldChange('agencyName', v)} />
                    <EditableField label="Notaire" value={editedData.notaryName} onChange={(v) => handleFieldChange('notaryName', v)} />
                  </div>
                </div>

                {/* Dates */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <span className="text-lg">📅</span> Dates clés
                  </h4>
                  <div className="space-y-3">
                    <EditableField label="Signature promesse" value={editedData.signatureDate} onChange={(v) => handleFieldChange('signatureDate', v)} type="date" />
                    <EditableField label="Date limite vente définitive" value={editedData.finalSaleDeadline} onChange={(v) => handleFieldChange('finalSaleDeadline', v)} type="date" />
                  </div>
                </div>
              </div>

              {/* Uncertain fields warning */}
              {Object.keys(uncertainFields).length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-orange-700">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Données à vérifier (signalées en orange) :</span>
                  </div>
                  <ul className="text-sm text-orange-600 mt-1 list-disc list-inside">
                    {Object.entries(uncertainFields).map(([field, reason]) => (
                      <li key={field}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Missing fields warning */}
              {missingFields.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Complétez les champs manquants :</span>
                  </div>
                  <p className="text-sm text-red-600 mt-1">{missingFields.join(', ')}</p>
                </div>
              )}

              {/* Extra charge */}
              {extractedData?.extra_charge > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-700">
                    <strong>+{extractedData.extra_charge}€</strong> pour l'analyse de la promesse de vente.
                  </p>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Important:</strong> Vérifiez toujours les données extraites.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 flex justify-between">
          {showPreview ? (
            <>
              <Button variant="outline" onClick={() => setShowPreview(false)}>Retour</Button>
              <Button onClick={handleApplyData} className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Appliquer ces données
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <div className="text-sm text-slate-500 flex items-center gap-1">
                <Sparkles className="h-4 w-4" /> Propulsé par l'IA
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
